import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getUtm, getSid } from "../lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PlayerAuthContext = createContext(null);

export const PlayerAuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("playerToken") || "");
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setPlayer(data))
      .catch(() => { localStorage.removeItem("playerToken"); setToken(""); })
      .finally(() => setLoading(false));
  }, [token]);

  const applyAuth = (data) => {
    localStorage.setItem("playerToken", data.access_token);
    localStorage.setItem("noSoccerUserId", data.player.id);
    setToken(data.access_token);
    setPlayer(data.player);
  };

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    applyAuth(data);
    return data.player;
  }, []);

  const register = useCallback(async (token, password, name, share_consent) => {
    const { data } = await axios.post(`${API}/auth/register`, { token, password, name, share_consent });
    applyAuth(data);
    return data.player;
  }, []);

  const redeem = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/auth/redeem`, payload);
    applyAuth(data);
    return data.player;
  }, []);

  const setupInfo = useCallback(async (token) => {
    const { data } = await axios.get(`${API}/auth/setup-info`, { params: { token } });
    return data;
  }, []);

  const buy = useCallback(async (early = false, tier = "ebook") => {
    const { data } = await axios.post(`${API}/payments/checkout`, { origin_url: window.location.origin, early, tier, utm: getUtm(), sid: getSid() });
    window.location.href = data.checkout_url;
  }, []);

  const openBillingPortal = useCallback(async () => {
    const { data } = await axios.post(`${API}/payments/portal`, { origin_url: window.location.origin },
      { headers: { Authorization: `Bearer ${token}` } });
    window.location.href = data.portal_url;
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("playerToken");
    setToken(""); setPlayer(null);
  }, []);

  const updateProfile = useCallback(async (fields) => {
    const { data } = await axios.put(`${API}/me`, fields, { headers: { Authorization: `Bearer ${token}` } });
    setPlayer(data);
    return data;
  }, [token]);

  const refresh = useCallback(async () => {
    if (!token) return null;
    const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    setPlayer(data);
    return data;
  }, [token]);

  const householdProfiles = useCallback(async () => {
    const { data } = await axios.get(`${API}/household/profiles`, { headers: { Authorization: `Bearer ${token}` } });
    return data;
  }, [token]);

  const addChild = useCallback(async (display_name, jersey_number) => {
    const { data } = await axios.post(`${API}/household/players`, { display_name, jersey_number },
      { headers: { Authorization: `Bearer ${token}` } });
    return data.profile;
  }, [token]);

  const switchProfile = useCallback(async (profileId) => {
    const { data } = await axios.post(`${API}/household/switch`, { profile_id: profileId },
      { headers: { Authorization: `Bearer ${token}` } });
    localStorage.setItem("playerToken", data.access_token);
    localStorage.setItem("noSoccerUserId", data.player.id);
    setToken(data.access_token);
    setPlayer(data.player);
    return data.player;
  }, [token]);

  const uploadAvatar = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await axios.post(`${API}/me/avatar`, fd, { headers: { Authorization: `Bearer ${token}` } });
    const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    setPlayer(data);
    return data;
  }, [token]);

  const removeAvatar = useCallback(async () => {
    await axios.delete(`${API}/me/avatar`, { headers: { Authorization: `Bearer ${token}` } });
    const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    setPlayer(data);
    return data;
  }, [token]);

  const hasAccess = !!player?.has_access;
  const coachPreview = !!localStorage.getItem("coachToken");
  const canRead = hasAccess || coachPreview;

  return (
    <PlayerAuthContext.Provider value={{ token, player, loading, hasAccess, coachPreview, canRead, login, register, redeem, setupInfo, buy, openBillingPortal, logout, updateProfile, uploadAvatar, removeAvatar, refresh, householdProfiles, addChild, switchProfile }}>
      {children}
    </PlayerAuthContext.Provider>
  );
};

export const usePlayerAuth = () => useContext(PlayerAuthContext);
